import React, { useState, useEffect, useCallback } from 'react';
import { Save, FileText } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import AttendanceReportModal from './AttendanceReportModal';
import useScopedClasses from '../useScopedClasses';

// Daily attendance per section. Everyone is Present unless marked —
// only exceptions (Absent / Late / Excused) are stored.
const STATUSES = ['Absent', 'Late', 'Excused'];
const STATUS_STYLE = {
  Absent: 'bg-red-600 text-white', Late: 'bg-amber-500 text-white', Excused: 'bg-blue-500 text-white'
};

const AttendanceSheet = ({ apiCall, year, showSuccess, showError }) => {
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => {
        setSections(s.filter(x => x.IsActive));
        setSectionCode(prev => s.some(x => x.SectionCode === prev) ? prev : (s[0]?.SectionCode || ''));
      }).catch(() => setSections([]));
  }, [apiCall, year, className]);

  const loadSheet = useCallback(async () => {
    if (!className || !date) return;
    try {
      const data = await apiCall(
        `/academic/attendance/sheet?academicYear=${year}&className=${encodeURIComponent(className)}&sectionCode=${encodeURIComponent(sectionCode)}&date=${date}`);
      setRows(data.map(r => ({ ...r, Notes: r.Notes || '' })));
      setDirty(false);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, sectionCode, date, showError]);

  useEffect(() => { loadSheet(); }, [loadSheet]);

  const cycle = (studentId) => {
    setRows(rs => rs.map(r => {
      if (r.StudentID !== studentId) return r;
      const idx = STATUSES.indexOf(r.Status);
      const next = idx === -1 ? STATUSES[0] : (idx === STATUSES.length - 1 ? null : STATUSES[idx + 1]);
      return { ...r, Status: next };
    }));
    setDirty(true);
  };

  const save = async () => {
    try {
      const r = await apiCall('/academic/attendance/sheet', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: year, className, sectionCode, date,
          entries: rows.map(x => ({ studentId: x.StudentID, status: x.Status || null, notes: x.Notes || null }))
        })
      });
      showSuccess(r.message);
      loadSheet();
    } catch (err) { showError(err.message); }
  };

  const exceptions = rows.filter(r => r.Status).length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {visibleClasses.length === 0 && <option value="">No classes</option>}
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All sections</option>
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        <span className="text-sm text-gray-500">{rows.length} students · {exceptions} exception(s)</span>
        <div className="flex-1" />
        <button
          onClick={() => setShowReport(true)}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-1"
        >
          <FileText size={15} /> Report
        </button>
        <Gate permission="academic.attendance.record" label="Save Attendance">
          <button
            onClick={save} disabled={!dirty}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Save size={15} /> Save
          </button>
        </Gate>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-40">Status (click to cycle)</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.StudentID} className="hover:bg-gray-50">
                <td className="px-3 py-1.5 font-medium text-gray-800">
                  {r.LastName} {r.FirstName}
                  {!sectionCode && r.Section && <span className="ml-2 text-xs text-gray-400">({r.Section})</span>}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button
                    onClick={() => cycle(r.StudentID)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold min-w-[90px] ${r.Status ? STATUS_STYLE[r.Status] : 'bg-green-100 text-green-700'}`}
                  >
                    {r.Status || 'Present'}
                  </button>
                </td>
                <td className="px-3 py-1.5">
                  {r.Status && (
                    <input
                      type="text" value={r.Notes}
                      onChange={e => { setRows(rs => rs.map(x => x.StudentID === r.StudentID ? { ...x, Notes: e.target.value } : x)); setDirty(true); }}
                      placeholder="optional note"
                      className="w-full border rounded px-2 py-1 text-xs"
                    />
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="3" className="text-center text-gray-500 py-6">No students.</td></tr>}
          </tbody>
        </table>
      </div>

      {showReport && (
        <AttendanceReportModal
          apiCall={apiCall} year={year} className={className} sectionCode={sectionCode}
          onClose={() => setShowReport(false)} showError={showError}
        />
      )}
    </div>
  );
};

export default AttendanceSheet;
